import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Default empty record shape
const emptyRecord = (pathname = '') => ({
    pathname,
    baseTable: null,        // { page, per_page, sort, dir, search, ... }
    customParams: null      // page-level custom filters (e.g. work orders extra filters)
});

const useFilterStore = create(
    persist(
        (set, get) => ({
            // Map of filters by pathname
            filtersByPath: {},
            lastPathname: '',

            // Set (or merge) filters for a path
            setFilters: (pathname, filters) => {
                set(state => {
                    const { lastPathname } = state;
                    let filtersByPath = state.filtersByPath;
                    // Determine if new pathname is unrelated to previous (not same or parent/child)
                    const isRelated = lastPathname && (
                        pathname === lastPathname ||
                        pathname.startsWith(lastPathname + '/') ||
                        lastPathname.startsWith(pathname + '/')
                    );
                    if (!isRelated && lastPathname) {
                        // Clear all previous filters when navigating to an unrelated section
                        filtersByPath = {};
                    }
                    return {
                        filtersByPath: {
                            ...filtersByPath,
                            [pathname]: {
                                ...(filtersByPath[pathname] || emptyRecord(pathname)),
                                ...filters,
                                pathname
                            }
                        },
                        lastPathname: pathname
                    };
                });
                // const record = get().filtersByPath[pathname];
                // console.log('Filters set for', pathname, '(auto-cleared others if unrelated)', record);
            },

            // Get filters for an exact path only
            getFilters: (pathname) => {
                const { filtersByPath } = get();
                if (filtersByPath[pathname]) return filtersByPath[pathname];
                return emptyRecord();
            },

            // Clear filters for a specific path (or all if omitted)
            clearFilters: (pathname) => {
                if (!pathname) {
                    set({ filtersByPath: {} });
                    return;
                }
                set(state => {
                    const copy = { ...state.filtersByPath };
                    delete copy[pathname];
                    return { filtersByPath: copy };
                });
            },

            // Update a single field inside baseTable or customParams for a path
            updateFilter: (pathname, group, field, value) => {
                set(state => {
                    const existing = state.filtersByPath[pathname] || emptyRecord(pathname);
                    const targetGroup = existing[group] || {};
                    return {
                        filtersByPath: {
                            ...state.filtersByPath,
                            [pathname]: {
                                ...existing,
                                [group]: {
                                    ...targetGroup,
                                    [field]: value
                                }
                            }
                        }
                    };
                });
            },

            // Migration utility (optional): promote old single currentFilters to new map
            migrateLegacy: () => {
                const state = get();
                if (state.currentFilters && state.currentFilters.pathname && !state.filtersByPath[state.currentFilters.pathname]) {
                    const { pathname, ...rest } = state.currentFilters;
                    set(state2 => ({
                        filtersByPath: {
                            ...state2.filtersByPath,
                            [pathname]: { pathname, baseTable: rest }
                        }
                    }));
                }
            }
        }),
        {
            name: 'base-table-filters',
            version: 2, // bump version for new structure
            // (Optional) persist middleware can implement migrate if needed later
        }
    )
);

export default useFilterStore;